import { useState } from 'react';
import { Button as BaseButton, Dialog, Input, Icon, Loading } from '../../../components/base';
import { Button } from '../../../components/Button';
import { DropdownHeader } from '../../../components/DropdownHeader';
import { ConnectorService, KmsSearchItem } from '../../../libs/services/ConnectorService';

interface KmsSearchDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (node: { label: string; url: string }) => void;
}

const connectorService = new ConnectorService();


const extractKmsErrorMessage = (e: unknown): string => {
  const raw = e instanceof Error ? e.message : String(e);
  const jsonStart = raw.indexOf('{');
  if (jsonStart >= 0) {
    try {
      const parsed = JSON.parse(raw.slice(jsonStart)) as { message?: string };
      if (parsed?.message) return parsed.message;
    } catch {
    }
  }
  return 'KMS 검색 중 오류가 발생했어요. 설정 > 도구에서 KMS 연결 상태를 확인해 주세요.';
};

const KmsSearchDialog: React.FC<KmsSearchDialogProps> = ({ isOpen, onClose, onSelect }) => {
  const [searchValue, setSearchValue] = useState('');
  const [items, setItems] = useState<KmsSearchItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');


  const runSearch = async () => {
    if (!searchValue.trim()) return;
    setLoading(true);
    setErrorMsg('');
    setItems([]); 
    setSelectedId(null);
    try {
      const result = await connectorService.searchKms(searchValue.trim());
      setItems(result);
      setSelectedId(null);
      setSearched(true);
    } catch (e) {
      console.error('KMS 검색 실패:', e);
      setItems([]);
      setSearched(true);
      setErrorMsg(extractKmsErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    const sel = items.find((n) => n.id === selectedId);
    if (sel) onSelect({ label: sel.title, url: sel.url });
    onClose();
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} className="kms-search-view" closeOnOutSide={true}>
      <div className="read-detail">
        <DropdownHeader title="KMS 검색" size="large">
          <Button className="ds-control-close" textBlind control onClick={onClose}>
            닫기
          </Button>
        </DropdownHeader>
        <div className="det-body">
          <div className="kms-search-bar">
            <Input
              className="search-input"
              size="large"
              variant="filled"
              placeholder="스페이스 명/페이지를 입력해주세요."
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
              }}
              onKeydown={(e) => {
                if (e.key === 'Enter') void runSearch();
              }}
              full
            />
            <BaseButton
              variant="normal"
              size="large"
              onClick={() => {
                void runSearch();
              }}
              disabled={!searchValue.trim() || loading}
            >
              검색
            </BaseButton>
          </div>

          <div className="kms-tree">
            <div className="kms-tree-title">검색 결과</div>
            {loading ? (
              <div className="kms-tree-loading">
                <Loading size="3" label="검색 중..." />
              </div>
            ) : (
              <ul className="kms-tree-list">
                {items.map((node) => (
                  <li
                    key={node.id}
                    className={`kms-tree-node ${node.type} ${selectedId === node.id ? 'is-selected' : ''} is-selectable`}
                    onClick={() => {
                      setSelectedId(node.id);
                    }}
                  >
                    <div className="kms-tree-main">
                      <div className="kms-tree-titleline">
                        <span className="kms-tree-label">{node.title}</span>
                        <span className={`kms-type-badge ${node.type}`}>
                          {node.type === 'space' ? '스페이스' : '페이지'}
                        </span>
                      </div>
                      <span className="kms-tree-url">{node.url}</span>
                    </div>

                    <a
                      className="kms-tree-open"
                      href={node.url}
                      target="_blank"
                      rel="noreferrer"
                      title="새 탭에서 열기"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <Icon name="link" />
                    </a>
                  </li>
                ))}
                {!searched && <li className="kms-empty">검색어를 입력해 검색해 주세요.</li>}
                {searched && items.length === 0 && !errorMsg && (
                  <li className="kms-empty">검색 결과가 없습니다.</li>
                )}
                {errorMsg && <li className="kms-empty">{errorMsg}</li>}
              </ul>
            )}
          </div>
        </div>
        <div className="det-foot">
          <div className="dsx-side-right">
            <BaseButton variant="outline" size="large" onClick={onClose}>
              취소
            </BaseButton>
            <BaseButton variant="primary" size="large" onClick={handleConfirm} disabled={!selectedId}>
              확인
            </BaseButton>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default KmsSearchDialog;
